import ObjectId from 'bson-objectid';
import { Avatar, Button, Col, Dropdown, Input, Row, Text } from 'components';
import { WebsiteView } from 'components/Post/components';
import { Api, formatText, theme, uploadMedia } from 'lib';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker } from 'screens/Admin/components';
import { LinkInput, Media, Youtube } from 'screens/Admin/components/PostPanel/components';

// Either Post or News View
const P4PReplayPost = ({
  post,
  setDataId,
  callback,
  closeModal,
  isEditing,
  isProcessing,
  setUploading,
  onSuccess,
  team
}) => {
  const [owner, setOwner] = useState(null),
    [author, setAuthor] = useState(null),
    [caption, setCaption] = useState(''),
    [media, setMedia] = useState([]),
    [metaData, setMetaData] = useState(null),
    [youtubeId, setYoutubeId] = useState(false),
    [metaLoading, setMetaLoading] = useState(false),
    [createdAt, setCreatedAt] = useState(''),
    [publishingDisabled, setPublishingDisabled] = useState(false),
    [editDisplayDisabled, setEditDisplayDisabled] = useState(false),
    [isNews, setIsNews] = useState(false),
    [errors, setErrors] = useState([]),
    displayOptions = ['Display as normal post', 'Display in Your Views on the News'],
    dispatch = useDispatch(),
    currentUser = useSelector((state) => state.user);

  useEffect(() => {
    if (post) {
      // Always use team as owner for P4P Replay
      setOwner(team || post?.owner || null);
      // Set author from post.author field
      setAuthor(post?.author || null);
      setCreatedAt(new Date(post?.createdAt) || null);
      setYoutubeId(post?.youtubeId || null);
      setCaption(post?.caption || '');
      setMedia(post?.media?.length ? post.media : []);
      setMetaData(post.metaData || null);
      setEditDisplayDisabled(true);
      setIsNews(!post.isPost || false);
    }

    if (isProcessing) {
      setPublishingDisabled(true);
    }
  }, [post, team]);

  useEffect(() => {
    setPublishingDisabled(!isProcessing);
  }, [isProcessing]);

  useEffect(() => {
    setPublishingDisabled(isNews && isNewsDisabled());
  }, [isNews, youtubeId, media, metaData]);

  function isNewsDisabled() {
    return !media?.length && !youtubeId && !metaData;
  }

  function displayNewsCaption() {
    return isNews ? displayOptions[1] : displayOptions[0];
  }

  async function submit() {
    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'createPost'
    });

    const newErrs = [];
    if (!owner) newErrs.push({ input: 'owner', err: 'Required' });

    if (isNewsDisabled() && !caption) {
      newErrs.push({
        input: 'media',
        err: 'At least one type of media or caption text is required'
      });
    }

    if (newErrs.length) {
      dispatch({
        type: 'LOADING_BUTTON',
        payload: null
      });

      return setErrors(newErrs);
    }

    const formatted = formatText(caption);

    const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
      mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

    if (media[0]?.file) return handleVideoPost(tags, mentioned, isNews);
    if (isEditing) return edit();

    const uploadedMedia = await Promise.all(media.map((x, i) => uploadMedia(x, i)));

    const data = {
      caption,
      metaData,
      tags,
      mentioned,
      media: uploadedMedia,
      owner: owner._id,
      author: currentUser._id,
      createdAt: createdAt ? new Date(createdAt) : new Date(),
      youtubeId: youtubeId || '',
      ownerType: 'team',
      type: 'p4p_replay'
    };

    const endpoint = isNews ? '/admin/createNewsView' : '/admin/createPost';
    const res = await Api.post(endpoint, data);

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    data.isPost = !isNews;

    dispatch({
      type: 'ADMIN_ADD_POST',
      payload: { ...data, owner, _id: res._id }
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: isNews ? 'Your Views on the News post created.' : 'Post created.' }
    });

    setEditDisplayDisabled(true);
    setDataId(res._id);
  }

  async function edit() {
    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'createPost'
    });

    const uploadedMedia = await Promise.all(
      media.map((x, i) => {
        if (!x.preview) return x;

        return uploadMedia(x, i);
      })
    );

    const formatted = formatText(caption);

    const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
      mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

    let data = {
      caption,
      metaData,
      tags,
      mentioned,
      media: uploadedMedia,
      owner: owner._id,
      author: currentUser._id,
      createdAt: createdAt ? new Date(createdAt) : new Date(),
      youtubeId: youtubeId || '',
      ownerType: 'team',
      type: 'p4p_replay'
    };

    let res;

    if (isNews) {
      res = await Api.put('/admin/editNewsView/' + post._id, data);
    } else {
      data.postId = post._id;
      res = await Api.post('/admin/editPost', data);
    }

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: 'ADMIN_EDIT_POST',
      payload: res
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: isNews ? 'Your Views on the News post updated.' : 'Post updated.' }
    });

    // Refresh the list after successful edit
    if (onSuccess) {
      onSuccess();
    }
  }

  async function handleVideoPost(tags, mentioned, isNews) {
    setUploading(true);
    let data = new FormData();
    const fileId = ObjectId().toString();

    data.append('owner', owner._id);
    data.append('author', currentUser._id);
    data.append('createdAt', createdAt ? new Date(createdAt) : new Date());
    data.append('ownerType', 'team');
    data.append('caption', caption);
    data.append('tags', JSON.stringify(tags));
    data.append('mentioned', JSON.stringify(mentioned));
    data.append('type', 'p4p_replay');
    if (isEditing) data.append('postId', post._id);

    const file = media[0].file;
    const fileType = file.type.split('/')[1];
    const fileName = `${fileId}-1.${fileType}`;

    data.append('fileName', fileName);
    data.append('video', file, fileName);

    const createEndpoint = isNews ? '/admin/createNewsView' : '/admin/createPost';
    const editEndpoint = isNews ? '/admin/editNewsView' : '/admin/editPost';
    let res;

    if (isEditing) {
      if (isNews) {
        res = await Api.put(editEndpoint + '/' + post._id, data);
      } else {
        data.postId = post._id;
        res = await Api.post(editEndpoint, data);
      }
    } else {
      res = await Api.post(createEndpoint, data);
    }

    setUploading(false);
    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: isEditing ? 'ADMIN_EDIT_POST' : 'ADMIN_ADD_POST',
      payload: { ...res, owner }
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: 'Upload successful. Processing your video.' }
    });

    setDataId(res._id);

    // Refresh the list after successful upload
    if (onSuccess) {
      onSuccess();
    }
  }

  return (
    <Col start between ht="calc(100% - 55px)" pad="0 24px">
      <Col wid="100%" noFlex>
        <Row between marg="24px 0 0">
          <Text marg="0 16px 4px" b2 med></Text>
        </Row>
        {editDisplayDisabled ? (
          <Input
            wid="100%"
            disabled
            defaultValue={displayNewsCaption()}
            bg={'rgba(121, 137, 160, 0.2)'}
          />
        ) : (
          <Dropdown
            title=""
            admin
            options={displayOptions}
            selected={displayNewsCaption()}
            handleSelect={(e) => {
              setIsNews(e === displayOptions[1]);
            }}
            bg="#fff"
            rounded
            wid="100%"
          />
        )}
        <Row between marg="24px 0 0">
          <Text marg="0 16px 4px" b2 med>
            Author
          </Text>
        </Row>
        {author && (
          <Row noFlex start marg="0 0 0">
            <Row center between wid="100%" pad="0 0">
              <Row noFlex center>
                <Avatar small user={author} />

                <Col marg="0 0 0 8px" noFlex>
                  <Text>{author?.name}</Text>

                  <Text c1 col={theme.GREY_60}>
                    @{author?.username}
                  </Text>
                </Col>
              </Row>
            </Row>
          </Row>
        )}

        {!author && (
          <Text c1 col={theme.GREY_60} marg="0 0 12px">
            No author information
          </Text>
        )}

        <Col marg="0 0 0" wid="100%">
          {metaData && (
            <WebsiteView
              isAdmin
              metaData={metaData}
              dimensions={552}
              remove={() => setMetaData(null)}
            />
          )}

          {!youtubeId && !metaData && (
            <Media
              media={media}
              setMedia={setMedia}
              isEditing={isEditing}
              isProcessing={isProcessing}
            />
          )}

          {!media?.length && !metaData && youtubeId && (
            <Youtube youtubeId={youtubeId} setYoutubeId={setYoutubeId} isEditing={isEditing} />
          )}

          {!media?.length && !metaData && !youtubeId && (
            <LinkInput
              metaData={metaData}
              youtubeId={youtubeId}
              setMetaData={setMetaData}
              setYoutubeId={setYoutubeId}
              setLoading={setMetaLoading}
              loading={metaLoading}
              err={errors.find((x) => x.input === 'media')}
            />
          )}
        </Col>

        <Row marg="24px 0 0" pos="relative">
          <Input
            title="Caption"
            wid="100%"
            multiline
            placeholder="Caption..."
            ht="100px"
            emoji
            value={caption}
            onChange={({ target }) => setCaption(target.value)}
            error={errors.find((x) => x.input === 'media')?.err}
          />
        </Row>

        <Text marg="24px 16px 4px" b2 med>
          Publish date
        </Text>

        <DatePicker
          selected={createdAt}
          handleSelect={setCreatedAt}
          title={!createdAt && 'Now'}
          marg="0 16px 0"
        />
      </Col>

      <Row pad="54px 0 24px">
        <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

        <Button
          big
          onClick={submit}
          disabled={publishingDisabled}
          text={isEditing ? 'Edit' : 'Publish'}
          bg={theme.SECONDARY}
          action="createPost"
        />
      </Row>
    </Col>
  );
};

export default P4PReplayPost;
